import { RADashboardCardId } from '@/constants/ReportDashboard/cards-enums'
import { FADashboardType } from '@/constants/ReportDashboard/dashboards/dashboard-types'
import { useTheme } from '@/contexts/RADashboardThemeContext/hooks'
import { RADashboardLayout } from '@/interfaces/raDashboard.interface'
import CloseIcon from '@mui/icons-material/Close'
import { Box, IconButton } from '@mui/material'
import React, { useMemo, useState } from 'react'
import type { EventCallback, Layout } from 'react-grid-layout'
import GridLayout, { useContainerWidth } from 'react-grid-layout'
import {
  applyCardsOrderChange,
  applyResizeChange,
  cardsOrderToGridLayout,
  GUI_CANVAS_COLS,
  hasLayoutItemMoved,
  removeCardFromGridLayout,
} from '../Editor/DashboardGuiCanvasUtils'
import { CardRenderer } from './CardRenderer'

type DashboardEditGridProps = {
  layout: RADashboardLayout
  selectedTab: string
  dashboardType: FADashboardType
  setLayout: (newLayout: RADashboardLayout) => void
}

const DashboardEditGrid: React.FC<DashboardEditGridProps> = (props) => {
  const { layout, selectedTab, dashboardType, setLayout } = props
  const theme = useTheme()
  const { width, mounted, containerRef } = useContainerWidth({ measureBeforeMount: true })
  const gridUnit = width / GUI_CANVAS_COLS

  const cards = useMemo(
    () => layout.tabLayouts[selectedTab]?.cardsOrder ?? [],
    [layout, selectedTab],
  )

  // Key only on real card IDs so that spacer changes elsewhere don't remount the grid.
  const cardKey = cards.filter((id) => !id.startsWith('empty_')).join(',')

  const canvasStyle = {
    width: '100%',
    minHeight: 400,
    marginBottom: '20px',
    borderRadius: '8px',
    border: `1px solid ${theme.border}`,
    backgroundColor: theme.tabBackground,
    backgroundImage: `radial-gradient(circle, ${theme.textSecondary} 1px, transparent 1px)`,
    backgroundSize: `${gridUnit / 4}px ${gridUnit / 4}px`,
  }

  return (
    <Box sx={canvasStyle} ref={containerRef}>
      {mounted && cards.length > 0 && (
        <EditableCardGrid
          key={cardKey}
          width={width}
          gridUnit={gridUnit}
          cards={cards}
          layout={layout}
          selectedTab={selectedTab}
          setLayout={setLayout}
          dashboardType={dashboardType}
        />
      )}
    </Box>
  )
}

type EditableCardGridProps = {
  width: number
  gridUnit: number
  cards: (RADashboardCardId | string)[]
  layout: RADashboardLayout
  selectedTab: string
  setLayout: (newLayout: RADashboardLayout) => void
  dashboardType: FADashboardType
}

// Drag/resize behavior mirrors GridCanvas in DashboardGuiEditor/DashboardGuiCanvas.tsx,
// but renders the real CardRenderer content (kept non-interactive) instead of a placeholder label.
const EditableCardGrid: React.FC<EditableCardGridProps> = ({
  width,
  gridUnit,
  cards,
  layout,
  selectedTab,
  setLayout,
  dashboardType,
}) => {
  const [gridLayout, setGridLayout] = useState<Layout>(() =>
    cardsOrderToGridLayout(cards, layout.cardSettings).filter((item) => !item.i.startsWith('empty_')),
  )

  const handleLayoutChange = (newLayout: Layout) => {
    setGridLayout((currentLayout) => {
      if (currentLayout.length !== newLayout.length) return newLayout
      if (!hasLayoutItemMoved(currentLayout, newLayout)) return currentLayout

      setLayout(applyCardsOrderChange(layout, selectedTab, newLayout))

      return newLayout
    })
  }

  const handleResizeStop: EventCallback = (newLayout, _oldItem, resizedItem) => {
    if (!resizedItem) return
    setGridLayout(newLayout)
    setLayout(applyResizeChange(layout, selectedTab, newLayout, resizedItem))
  }

  const handleRemoveCard = (cardId: string) => {
    const newGridLayout = removeCardFromGridLayout(gridLayout, cardId)
    setGridLayout(newGridLayout)
    setLayout(applyCardsOrderChange(layout, selectedTab, newGridLayout))
  }

  return (
    <GridLayout
      width={width}
      gridConfig={{ cols: GUI_CANVAS_COLS, rowHeight: gridUnit }}
      dragConfig={{ enabled: true }}
      resizeConfig={{ enabled: true, handles: ['se'] }}
      layout={gridLayout}
      onLayoutChange={handleLayoutChange}
      onResizeStop={handleResizeStop}
    >
      {cards.filter((id) => !id.startsWith('empty_')).map((id) => {
        const cardId = id as RADashboardCardId
        const item = gridLayout.find((gridItem) => gridItem.i === cardId)
        const cardProps = {
          key: cardId,
          cardId,
          w: item?.w ?? 0,
          h: item?.h ?? 0,
          gridUnitSize: gridUnit,
          cardSettings: layout.cardSettings?.[cardId] ?? { size: '' },
        }
        return (
          <EditableCardWrapper
            key={cardId}
            cardId={cardId}
            onRemoveCard={handleRemoveCard}
            content={
              <CardRenderer cardProps={cardProps} dashboardType={dashboardType} />
            }
          />
        )
      })}
    </GridLayout>
  )
}

type EditableCardWrapperProps = {
  cardId: string
  onRemoveCard: (cardId: string) => void
  content: React.ReactNode
  children?: React.ReactNode
}

// react-grid-layout clones this element to inject its ref, className and positioning
// style (forwarded via ref + ...rest), and react-resizable injects the resize handle as
// this element's `children` prop — so `content` (the card itself) must be passed as a
// separate prop, leaving `children` free for the handle to appear via {children} below.
const EditableCardWrapper = React.forwardRef<HTMLDivElement, EditableCardWrapperProps>((props, ref) => {
  const { cardId, onRemoveCard, content, children, ...rest } = props
  const theme = useTheme()

  return (
    <Box
      ref={ref}
      {...rest}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        cursor: 'grab',
        userSelect: 'none',
        '& > .react-resizable-handle': {
          position: 'absolute',
          bottom: 0,
          right: 0,
          width: 20,
          height: 20,
          cursor: 'se-resize',
          zIndex: 2,
          '&::after': {
            content: '""',
            position: 'absolute',
            right: 3,
            bottom: 3,
            width: 6,
            height: 6,
            borderRight: `2px solid ${theme.textSecondary}`,
            borderBottom: `2px solid ${theme.textSecondary}`,
          },
        },
      }}
    >
      <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
        {content}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 1,
            borderRadius: '8px',
            backgroundColor: theme.cardBackground,
            opacity: 0.5,
          }}
        />
      </Box>
      <IconButton
        size="small"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => { e.stopPropagation(); onRemoveCard(cardId) }}
        sx={{ position: 'absolute', top: 2, right: 2, zIndex: 2, color: theme.textSecondary }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
      {children}
    </Box>
  )
})
EditableCardWrapper.displayName = 'EditableCardWrapper'

export default DashboardEditGrid
